import type { RenderContext } from '../types';
import { Fade } from '@florencelabs/fade';
import { useMemo, useEffect, ReactElement, useRef, useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { NoOnlineAvailabilityAtLocation } from '@provider-directory/components/availability/no-online-availability-at-location.component';
import { TimeSlotPicker } from '@provider-directory/components/time-slot-picker/time-slot-picker.component';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { useCachedSlots } from '@provider-directory/contexts/CachedSlotsContext';
import { useLiveSlots } from '@provider-directory/contexts/LiveSlotsContext';
import {
  computeNoAvailabilityScenario,
  useNoAvailabilityScenario,
} from '@provider-directory/contexts/NoAvailabilityScenarioContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { parseCalendarDate } from '@provider-directory/functions/date';
import {
  findVisitTypeIdByRfv,
  getProviderLocationPhoneById,
} from '@provider-directory/models/provider.model';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import { ProviderTimeSlot } from '@provider-directory/types/scheduling.contracts';
import { AppointmentTimeFormFieldValues } from '../select-appointment-form-values';
import { NoAvailabilityBanner } from './no-availability-banner';
import { TimeSlotsSkeleton } from './select-appointment-skeletons.component';

interface Props {
  onAnimationComplete: () => void;
  onClose?: () => void;
  providerId: string;
  providerLocations: ProviderResource['locations'];
  reasonForVisit: string;
  renderContext: RenderContext;
}

export const ControlledAppointmentSlots = ({
  onAnimationComplete,
  onClose: _onClose,
  providerId,
  providerLocations,
  reasonForVisit,
  renderContext,
}: Props): ReactElement => {
  const { rfvProvidersCount } = useNoAvailabilityScenario();
  const { t } = useTranslation();
  const {
    control,
    watch,
    setValue,
    formState: { submitCount },
  } = useFormContext<AppointmentTimeFormFieldValues>();
  const { appointment, updateAppointment } = useAppointment();
  const { field, fieldState } = useController<
    AppointmentTimeFormFieldValues,
    'slotTime'
  >({
    name: 'slotTime',
    rules: { required: t('schedule.noTimeSlotSelectedError') },
    control,
  });
  const departmentId = watch('departmentId');
  const slotDateString = watch('slotDate');
  const slotDate = useMemo(
    () => parseCalendarDate(slotDateString ?? ''),
    [slotDateString],
  );
  const {
    cachedSlots,
    isLoading: isLoadingCachedSlots,
    nearestAvailability,
  } = useCachedSlots();
  const {
    keyedSlots: liveSlots,
    isLoading: isLoadingLiveSlots,
    fetchSlotsForDay,
  } = useLiveSlots();
  const { provider } = useProvider();
  const currentLocation = useMemo(
    () =>
      providerLocations.find((loc) => loc.ehrExternalId === departmentId) ??
      null,
    [providerLocations, departmentId],
  );
  const analytics = useAnalytics();
  const {
    fetchRfvProvidersCount,
    openNoRfvProvidersModal,
    isLoading: isLoadingRfvProviders,
  } = useNoAvailabilityScenario();

  const lastFetchedRfvRef = useRef<string | null>(null);
  const errorMessageRef = useRef<HTMLParagraphElement>(null);
  const firedDiscrepancyEventsRef = useRef<Set<string>>(new Set());

  const currentSlots = useMemo<ProviderTimeSlot[]>(() => {
    if (!slotDate || !departmentId) {
      return [];
    }
    return liveSlots[departmentId]?.[slotDate.toString()] ?? [];
  }, [slotDate, departmentId, liveSlots]);

  const isLoadingSlots = isLoadingCachedSlots || isLoadingLiveSlots;

  useEffect(() => {
    // Only fetch or compute no-availability when there are no slots for the selected date/location.
    if (isLoadingSlots || currentSlots.length > 0) return;
    // If the reason for visit is missing or unchanged, don't fetch again.
    if (!reasonForVisit || lastFetchedRfvRef.current === reasonForVisit) return;

    lastFetchedRfvRef.current = reasonForVisit;

    const fetchCount = async () => {
      try {
        await fetchRfvProvidersCount(reasonForVisit);
      } catch {
        // Ignore errors
      }
    };

    void fetchCount();
  }, [isLoadingSlots, currentSlots, reasonForVisit, fetchRfvProvidersCount]);

  const noRFVAvailabilityProps = useMemo(
    () =>
      isLoadingSlots || currentSlots.length > 0
        ? undefined
        : computeNoAvailabilityScenario(
            {
              providerId,
              reasonForVisit,
              departmentId,
              slotDateString,
              cachedSlots,
            },
            rfvProvidersCount,
          ),
    [
      providerId,
      departmentId,
      reasonForVisit,
      slotDateString,
      rfvProvidersCount,
      cachedSlots,
      currentSlots.length,
      isLoadingSlots,
    ],
  );

  const handleNavigateToNearestSlot = useCallback(() => {
    const nearestSlot = noRFVAvailabilityProps?.nearestSlot;
    if (!nearestSlot) return;

    const newDate = parseCalendarDate(nearestSlot.date);
    if (!newDate) return;

    setValue('departmentId', nearestSlot.locationId);
    setValue('slotDate', nearestSlot.date);

    updateAppointment({
      departmentId: nearestSlot.locationId,
      slotDate: nearestSlot.date,
    });

    fetchSlotsForDay?.(newDate);
  }, [noRFVAvailabilityProps, setValue, updateAppointment, fetchSlotsForDay]);

  // Open the no providers modal if the no availability scenario is "no-providers"
  useEffect(() => {
    if (noRFVAvailabilityProps?.type !== 'no-providers' || !reasonForVisit)
      return;

    openNoRfvProvidersModal(reasonForVisit);
  }, [noRFVAvailabilityProps?.type, reasonForVisit, openNoRfvProvidersModal]);

  // clear selected time on day change
  useEffect(() => {
    setValue('slotTime', null);
    setValue('visitTypeId', null);
  }, [slotDate, setValue]);

  useEffect(() => {
    if (submitCount && fieldState.invalid) {
      errorMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [submitCount, fieldState.invalid]);

  useEffect(() => {
    if (slotDate && fetchSlotsForDay) {
      fetchSlotsForDay(slotDate);
    }
  }, [slotDate, fetchSlotsForDay]);

  const slotTime = watch('slotTime');

  useEffect(() => {
    if (!slotTime || !reasonForVisit) {
      return;
    }

    const selectedSlot = currentSlots.find(
      (slot: ProviderTimeSlot) => slot.start === slotTime,
    );
    const visitTypeId =
      selectedSlot?.reasonsForVisit[reasonForVisit]?.visitTypeId ??
      findVisitTypeIdByRfv(
        appointment.provider,
        appointment.departmentId ?? '',
        appointment.reasonForVisit ?? '',
      );

    setValue('visitTypeId', visitTypeId);
  }, [
    slotTime,
    currentSlots,
    reasonForVisit,
    appointment.provider,
    appointment.departmentId,
    appointment.reasonForVisit,
    setValue,
  ]);

  // Track discrepancy between cached and live slots
  useEffect(() => {
    if (isLoadingSlots || !slotDate || !departmentId) {
      return;
    }

    const dateString = slotDate.toString();
    const eventKey = `${departmentId}-${dateString}`;

    // Check if we already fired this event for this date+location
    if (firedDiscrepancyEventsRef.current.has(eventKey)) {
      return;
    }

    // Check for discrepancy: cached slots exist but no live slots
    const cachedSlotsCount = cachedSlots?.[departmentId]?.[dateString] ?? 0;
    const liveSlotsCount = currentSlots.length;

    if (cachedSlotsCount > 0 && liveSlotsCount === 0 && provider) {
      analytics.cachedLiveSlotsDiscrepancyEvent({
        provider,
        location: currentLocation ?? undefined,
        selectedDate: dateString,
        cachedSlotsCount,
        liveSlotsCount,
        reasonForVisit,
        linkContextPage: 'SchedulingSelectTime',
      });

      firedDiscrepancyEventsRef.current.add(eventKey);
    }
  }, [
    isLoadingSlots,
    slotDate,
    departmentId,
    cachedSlots,
    currentSlots.length,
    provider,
    currentLocation,
    analytics,
    reasonForVisit,
  ]);

  return isLoadingSlots || isLoadingRfvProviders ? (
    <TimeSlotsSkeleton />
  ) : (
    <Fade
      fadeOn={slotDate?.toString()}
      onAnimationComplete={onAnimationComplete}
    >
      {currentSlots.length > 0 ? (
        <TimeSlotPicker timeSlots={currentSlots} isRequired={true} {...field} />
      ) : noRFVAvailabilityProps?.type === 'location' ? (
        <NoOnlineAvailabilityAtLocation
          provider={provider}
          location={currentLocation}
          nearestAvailability={nearestAvailability}
          onViewNextAvailable={handleNavigateToNearestSlot}
          variant={renderContext === 'decision-tree' ? 'tonal' : 'system'}
        />
      ) : (
        <NoAvailabilityBanner
          reasonForVisit={reasonForVisit}
          type={noRFVAvailabilityProps?.type ?? 'other-providers'}
          locationPhoneNumber={getProviderLocationPhoneById(
            providerLocations,
            departmentId,
          )}
          onNavigateToSlot={handleNavigateToNearestSlot}
          variant={renderContext === 'decision-tree' ? 'tonal' : 'system'}
        />
      )}
      {fieldState.error && (
        <p
          ref={errorMessageRef}
          data-testid={'error-message'}
          className={'pt-6 text-display-2xsmall font-medium text-red-600'}
        >
          {fieldState.error.message}
        </p>
      )}
    </Fade>
  );
};
